import React from 'react';
import { Square, Check } from 'lucide-react';
import { Message } from '../../types';

interface PromptStatusButtonsProps {
  prompt: Message;
  promptNumber: number;
  isProcessing: boolean;
  isFocused: boolean;
  onStop: (promptId: string) => void;
  onScrollToPrompt: (promptId: string) => void;
  /** Optional callback when any interaction occurs (for questionmark tracking) */
  onInteract?: () => void;
}

/**
 * PHASE 1: Minimal prompt status button for each user prompt
 * 
 * Design: Subtle, understated, similar size to response dots
 * - While processing (before EOP): White stop icon with subtle white spinner on gray background
 * - After EOP (completed): Text label + tiny checkmark (P1 ✓)
 * - After Stop (stopped early): Text label + tiny square (P1 ⏹)
 * - Focused prompt: Darker background for current focus
 * Labels are 1-indexed (P1, P2, P3, etc.)
 */
export function PromptStatusButtons({
  prompt,
  promptNumber,
  isProcessing,
  isFocused,
  onStop,
  onScrollToPrompt,
  onInteract,
}: PromptStatusButtonsProps) {
  const label = prompt.sendConfig?.promptSource === 'url' 
    ? 'URL' 
    : `P${promptNumber}`;

  // Show Stop button if currently processing (before EOP)
  if (isProcessing) {
    return (
      <button
        type="button"
        onClick={() => {
          onStop(prompt.id);
          if (onInteract) onInteract();
        }}
        className={`relative flex items-center justify-center h-5 w-8 px-1.5 rounded transition-all ${
          isFocused 
            ? 'bg-foreground/20 hover:bg-foreground/25' 
            : 'bg-foreground/10 hover:bg-foreground/15'
        }`}
        title="Stop processing (irreversible)"
      >
        {/* Subtle spinner - segmented ring with spacing from the stop square */}
        <div className="relative flex items-center justify-center">
          {/* Segmented spinner using dashed border for a dotted appearance */}
          <div className="absolute h-4 w-4 rounded-full border-2 border-dashed border-muted-foreground/60 border-t-muted-foreground animate-spin" 
               style={{ borderSpacing: '2px' }} />
          
          {/* Stop icon - very small, centered */}
          <Square className="h-2 w-2 fill-current text-muted-foreground" />
        </div>
      </button>
    );
  }

  // Show text label after EOP or stopped
  // Determine which icon to show
  const wasStoppedEarly = prompt.stoppedBeforeEOP === true;
  const reachedEOP = prompt.eop === true && !wasStoppedEarly;
  
  const tooltipText = wasStoppedEarly 
    ? `${label} (stopped early)` 
    : reachedEOP 
    ? `${label} (completed)` 
    : `Go to ${label}`;
  
  return (
    <button
      type="button"
      onClick={() => {
        onScrollToPrompt(prompt.id);
        if (onInteract) onInteract();
      }}
      className={`flex items-center justify-center gap-0.5 h-5 w-10 px-1.5 rounded transition-all cursor-pointer ${
        isFocused 
          ? 'bg-foreground/20 hover:bg-foreground/25' 
          : 'bg-foreground/10 hover:bg-foreground/15'
      }`}
      title={tooltipText}
    >
      <span className="text-[10px] text-muted-foreground font-medium">{label}</span>
      {reachedEOP && <Check className="h-2 w-2 text-muted-foreground" strokeWidth={3} />}
      {wasStoppedEarly && <Square className="h-1.5 w-1.5 fill-current text-muted-foreground" />}
    </button>
  );
}

